// A sidebar folded to its icons (as wide as the rail). Every folded sidebar is
// built from these, so they look and behave the same.
//
// Promise: the top holds the sidebar's switcher folded to its mark, where the
// full sidebar has its switcher (Me has none: its rows start at the top). The lock that keeps the sidebar open is at the peek's top right,
// and `]` folds and unfolds it. Rows are 36×32 icon buttons named by aria-label; resting the mouse on
// them shows the full sidebar over the panel for a peek (names and all), which
// goes when the pointer leaves it. The current row has the same background and
// text color as a full row. A mark the full row shows at its end (unread, a count) is a dot on the
// icon's corner, laid over it so the row never changes size. What does not fit
// as an icon (favorites, labels) is one button that opens the full sidebar
// over the panel. The parts' `Ongoing` (the call one is in) sits at the foot.
import { Link } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import type { ComponentType, ReactNode } from "react";
import { Icon, iconOf } from "../ui/icon";
import { Tooltip } from "../ui/tooltip";
import { useShellFrame } from "./frame";
import { OngoingRows } from "./ongoing";
import type { ShellMenuItem } from "./outside/contract";
import {
  entryIcon,
  menuItems,
  useShellMenu,
  type WebEntryExtras,
} from "./outside/source";

const SettingsIcon = iconOf("settings");

export const ICON_ROW =
  "relative flex h-8 w-9 flex-none items-center justify-center rounded-[7px] text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg";
export const ICON_ROW_CURRENT = "bg-sidebar-active! text-sidebar-fg-strong";
/**
 * The line above and under the rows kept apart (the cloud's DM), in the same
 * column as the rows (1 px apart): the full sidebar and the folded column put
 * it at the same height, so neither the line nor the rows move as the sidebar
 * folds or grows.
 */
export const APART_RULE = "my-1 h-px flex-none bg-field-strong/60";

export function CollapsedColumn({
  label,
  head,
  children,
  foot,
}: {
  label: string;
  /** At the top, where the full sidebar has its switcher (`SidebarSwitcher` folded). None: the rows start at the top. */
  head?: ReactNode;
  /** The rows; the parts' `Ongoing` icons follow at the foot. */
  children: ReactNode;
  /** Under the `Ongoing` icons, where the full sidebar has its foot row. */
  foot?: ReactNode;
}) {
  const frame = useShellFrame();
  return (
    <nav
      aria-label={label}
      className="flex h-full min-h-0 flex-col items-center bg-sidebar pt-2.5"
    >
      {/* Every row where the full sidebar has its row: the head in the
          switcher's 36 px, then the rows 1 px apart from 8 px under it. The
          full sidebar grows out of this column (a peek, the drawer), so a
          row that sat elsewhere would jump as it grows. */}
      {/* Resting on the head peeks too (the peek shows the full row in its
          place, so its menu is still there); pressing it before then opens
          its menu and the peek waits for the next rest. */}
      {/* No head (Me): the rows start 4 px under the top, as the full
          sidebar's do. */}
      {head !== undefined && (
        <div
          className="flex h-9 flex-none items-center"
          onPointerEnter={(e) => frame.peek.start(e.pointerType)}
          onPointerLeave={frame.peek.cancel}
          onPointerDown={frame.peek.cancel}
        >
          {head}
        </div>
      )}
      {/* The scrollbar's gutter on both sides: on one side only, the icons
          would sit off the column's center and off the button above. */}
      <div
        className={`${head !== undefined ? "mt-2" : "mt-1"} flex min-h-0 w-full flex-1 flex-col items-center gap-px overflow-y-auto pb-4 [scrollbar-gutter:stable_both-edges]`}
        onPointerEnter={(e) => frame.peek.start(e.pointerType)}
        onPointerLeave={frame.peek.cancel}
      >
        {children}
      </div>
      <OngoingRows shape="icon" className={foot ? "" : "pb-2.5"} />
      {foot && <div className="flex flex-none pt-1 pb-2.5">{foot}</div>}
    </nav>
  );
}

/** A dot on an icon's corner for a mark the full row shows at its end. */
const CornerDot = () => (
  <span
    aria-hidden="true"
    className="absolute top-1 right-1 size-1.5 rounded-full bg-accent"
  />
);

export function IconNavRow({
  to,
  icon: IconComponent,
  label,
  mark,
  exact = false,
  current,
}: {
  to: string;
  icon?: ComponentType<{ className?: string }>;
  label: string;
  /** What the full row shows at its end; any mark becomes a dot. */
  mark?: ReactNode;
  exact?: boolean;
  /** Whether the row is the current place, when its link alone cannot tell (as `NavRow`'s). */
  current?: boolean;
}) {
  return (
    // A finger's long press tells the name (the icon has none beside it).
    <Tooltip text={label} hold>
      <Link
        to={to}
        aria-label={label}
        activeOptions={{ exact, includeSearch: false }}
        className={current ? `${ICON_ROW} ${ICON_ROW_CURRENT}` : ICON_ROW}
        aria-current={current ? "page" : undefined}
        activeProps={
          current === undefined
            ? { className: ICON_ROW_CURRENT, "aria-current": "page" }
            : {}
        }
      >
        {IconComponent ? (
          <IconComponent className="pointer-events-none" />
        ) : (
          <span className="text-[12px]">{label.slice(0, 1)}</span>
        )}
        {mark != null && mark !== false && <CornerDot />}
      </Link>
    </Tooltip>
  );
}

/** A button that opens the full sidebar over the panel (favorites, labels). */
export function IconDrawerRow({
  icon,
  label,
}: {
  icon: ReactNode;
  label: string;
}) {
  const frame = useShellFrame();
  return (
    <Tooltip text={label} hold>
      <button
        type="button"
        aria-label={label}
        // Only the folded column has it: it fades out as the full sidebar grows
        // over it (styles: `data-fold-only`).
        data-fold-only=""
        className={ICON_ROW}
        onClick={frame.openDrawer}
      >
        {icon}
      </button>
    </Tooltip>
  );
}

/** A thin gap between groups of icons (in place of a section's heading). */
export const IconGroupGap = () => <div aria-hidden="true" className="h-1.5" />;

/** Me's sidebar folded: the parts' nav, then favorites and labels behind one button. */
export function MeSidebarCollapsed() {
  const menu = useShellMenu();
  const items = menuItems(menu.menu, "me");
  const isApart = (item: ShellMenuItem) => !!menu.extras.get(item.id)?.apart;
  const apart = items.filter(isApart);
  return (
    <CollapsedColumn
      label={m.shell_me()}
      foot={
        menu.menu.rail ? undefined : (
          <IconNavRow
            to="/me/settings"
            icon={SettingsIcon}
            label={m.shell_settings()}
          />
        )
      }
    >
      {items
        .filter((item) => !isApart(item))
        .map((item) => (
          <MeNavIcon
            key={item.id}
            item={item}
            extras={menu.extras.get(item.id)}
          />
        ))}
      {apart.length > 0 ? (
        <>
          {/* The full sidebar's lines, above and under the rows kept apart. */}
          <div aria-hidden="true" className={`${APART_RULE} w-6`} />
          {apart.map((item) => (
            <MeNavIcon
              key={item.id}
              item={item}
              extras={menu.extras.get(item.id)}
            />
          ))}
          <div aria-hidden="true" className={`${APART_RULE} w-6`} />
        </>
      ) : (
        <IconGroupGap />
      )}
      <IconDrawerRow
        icon={<Icon kind="favorite" className="pointer-events-none" />}
        label={m.shell_favorites()}
      />
    </CollapsedColumn>
  );
}

function MeNavIcon({
  item,
  extras,
}: {
  item: ShellMenuItem;
  extras: WebEntryExtras | undefined;
}) {
  const mark = extras?.useBadge?.();
  return (
    <IconNavRow
      to={item.to ?? "/me"}
      icon={entryIcon(item, extras)}
      label={item.label}
      mark={mark}
    />
  );
}
